// src/App.jsx
import React, { Suspense, useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import NotFoundPage from "../src/pages/NotFoundPage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  CssBaseline,
  GlobalStyles,
  Paper,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

// Lazy pages
const LoginPage = React.lazy(() => import("../src/pages/LoginPage"));
const SidebarApp = React.lazy(() => import("../src/components/SidebarApp"));

// Brand colors (match Navbar/Sidebar)
const GRAD_LEFT = "rgba(7,141,238,0.96)";
const GRAD_RIGHT = "rgba(40, 139, 181, 0.96)";
const BRAND = "rgb(7,141,238)";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: BRAND },
    secondary: { main: "#68cdf9" },
    background: {
      default: "#f6f9fc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#475569",
    },
  },
  // shape: { borderRadius: 14 },
  typography: {
    fontFamily: [
      "Inter",
      "system-ui",
      "-apple-system",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "Noto Sans",
      "sans-serif",
      "Kanit"
    ].join(","),
    button: { textTransform: "none", fontWeight: 700 },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          // borderRadius: 16,
          boxShadow:
            "0 12px 30px rgba(7,141,238,0.08), 0 2px 10px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          // borderRadius: 12,
          paddingInline: 18,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 5,
          },
        },
      },
    },
  },
});

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1200); // splash สั้น ๆ พอชิล
    return () => clearTimeout(t);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Global gradient background + subtle noise */}
      <GlobalStyles
        styles={{
          body: {
            background: `linear-gradient(120deg, ${GRAD_LEFT}, ${GRAD_RIGHT}) fixed`,
            backgroundAttachment: "fixed",
          },
          "#root": {
            minHeight: "100svh",
            // แผ่นกระดาษทุกใบลอยบนพื้นหลัง gradient
          },
        }}
      />

      <Router>
        <ToastContainer />
        <Suspense fallback={<BrandedSplash />}>
          <Routes>
            <Route
              path="/"
              element={loading ? <BrandedSplash /> : <LoginPage />}
            />
            <Route path="*" element={<NotFoundPage />} />

            {/* ใช้ SidebarApp เป็น Shell ของทุกเพจหลังล็อกอิน */}
            <Route
              path="/HomePage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/link-order/:id"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/TackingWorkPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/AssignArticleReviewersPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/LookArticleMapPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/LookTableTimeLineTackingWorkPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/TableTimeLineTackingWorkPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/ArticleaArchivePage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/ManagePage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
            <Route
              path="/SettingWebPage"
              element={loading ? <BrandedSplash /> : <SidebarApp />}
            />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

/**
 * Splash/Loading จอเต็มแบบ glassmorphism ให้กลิ่นเดียวกับ Navbar/Sidebar
 */
function BrandedSplash() {
  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100svh",
        display: "grid",
        placeItems: "center",
        p: { xs: 2.5, sm: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          // glass look
          background: "rgba(255,255,255,0.14)",
          border: "1px solid rgba(255,255,255,0.35)",
          backdropFilter: "saturate(160%) blur(10px)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 74,
            height: 74,
            borderRadius: "50%",
            mb: 2,
            background:
              "linear-gradient(180deg, rgba(255,255,255,.35), rgba(255,255,255,.08))",
            border: "1px solid rgba(255,255,255,.45)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.6)",
          }}
        >
          <CircularProgress size={36} thickness={4} sx={{ color: "#fff" }} />
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontWeight: 800,
            letterSpacing: 0.2,
            mb: 0.5,
          }}
        >
          กำลังโหลดระบบ Intranet สตน.ทอ.
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
          โปรดรอสักครู่…
        </Typography>
      </Paper>
    </Box>
  );
}

export default App;
